import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, FloppyDisk, Database } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/text-area';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { delay: 0.1, duration: 0.2 } }
};

const modalVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  visible: { 
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] }
  },
  exit: { 
    opacity: 0, y: 20, scale: 0.98,
    transition: { duration: 0.2, ease: "easeOut" }
  }
};

export const ComplexAnswerContentModal = ({
  isOpen,
  onClose,
  variant,
  isInline = false
}) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize content when modal opens
  useEffect(() => {
    if (isOpen && variant) {
      setContent(variant.fullData?.content || '');
      setHasUnsavedChanges(false);
    }
  }, [isOpen, variant]);

  // Handle content changes
  const handleContentChange = (e) => {
    setContent(e.target.value);
    setHasUnsavedChanges(true);
  };

  // Handle save
  const handleSave = async () => {
    // Get the settings_id and grouping_id from variant structure
    const settingsId = variant?.parentSetting?.id || variant?.fullData?.settings_id || variant?.settings_id;
    const groupingId = variant?.id || variant?.fullData?.grouping_id || variant?.grouping_id;

    if (!settingsId || !groupingId) {
      toast.error('Missing variant information for save');
      return;
    }

    setIsSubmitting(true);

    try {
      // Update the specific grouping's content
      const { error } = await supabase
        .from('brain_complex_answers')
        .update({ content })
        .eq('settings_id', settingsId)
        .eq('grouping_id', groupingId);

      if (error) throw error;

      toast.success('Content updated successfully');
      setHasUnsavedChanges(false);

      // Always notify parent of successful save, but don't close modal for inline
      const saveResult = { 
        success: true, 
        updatedContent: content,
        settingsId,
        groupingId,
        shouldClose: !isInline // Only close if not inline
      };

      onClose(saveResult);
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose(false);
      }
    } else {
      onClose(false);
    }
  };

  if (!isOpen) return null;

  const renderContent = () => (
    <motion.div 
      className={cn(
        "bg-gray-50 flex flex-col overflow-hidden",
        isInline 
          ? "w-full h-full" 
          : "rounded-2xl shadow-2xl w-full max-w-3xl mx-2 sm:mx-4 min-h-[60vh] max-h-[90vh] relative border border-gray-200/50"
      )}
      variants={!isInline ? modalVariants : {}}
      initial={!isInline ? "hidden" : ""}
      animate={!isInline ? "visible" : ""}
      exit={!isInline ? "exit" : ""}
      onClick={e => e.stopPropagation()}
    >
      {/* Elegant Header with Gradient */}
      <motion.div 
        className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200/50 flex justify-between items-center bg-white/70 backdrop-blur-sm z-20"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <div className="flex items-center">
          <motion.div 
            className="w-10 h-10 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-inner flex items-center justify-center mr-3 flex-shrink-0"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <Database className="h-5 w-5 text-white" />
          </motion.div>
          <div>
            <h2 className="text-lg sm:text-xl font-light text-gray-900 tracking-tight">
              Edit Content: {variant?.name}
            </h2>
            <p className="text-sm text-gray-500 mt-1 font-light">
              Location variant for "{variant?.parentSetting?.name}"
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {!isInline && (
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              size="sm"
              className="border-gray-200/50 bg-white/50 backdrop-blur-sm hover:bg-white text-gray-700 rounded-xl shadow-inner"
            >
              <span className="text-xs sm:text-sm font-light">Cancel</span>
            </Button>
          )}
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              type="button"
              disabled={isSubmitting || !hasUnsavedChanges}
              onClick={handleSave}
              size="sm"
              className="bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white min-w-[100px] shadow-inner rounded-xl"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-1">
                  <svg className="animate-spin h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-xs sm:text-sm font-light">Saving</span>
                </span>
              ) : (
                <span className="text-xs sm:text-sm font-light">Save</span>
              )}
            </Button>
          </motion.div>
        </div>
      </motion.div>

      {/* Content with Enhanced Styling */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
        <motion.div 
          className="space-y-4 sm:space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <motion.div 
            className="space-y-1.5 sm:space-y-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.3 }}
          >
            <Label htmlFor="content" className="text-xs sm:text-sm font-light text-gray-700 tracking-wide flex items-center">
              Content <span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Textarea
              id="content"
              value={content}
              onChange={handleContentChange}
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                  const textarea = e.target;
                  const start = textarea.selectionStart;
                  const end = textarea.selectionEnd;
                  const currentText = content;

                  // Insert tab character at cursor position
                  const newText = currentText.substring(0, start) + '\t' + currentText.substring(end);

                  setContent(newText);
                  setHasUnsavedChanges(true);

                  // Move cursor after the inserted tab
                  setTimeout(() => {
                    textarea.selectionStart = textarea.selectionEnd = start + 1;
                  }, 0);
                }
              }}
              placeholder="Enter the complex answer content for this location variant..."
              className="min-h-[400px] text-sm border-gray-200/50 bg-white/50 backdrop-blur-sm focus:bg-white transition-all duration-200 font-mono rounded-lg shadow-inner"
            />
          </motion.div>

          <motion.div 
            className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/50 rounded-xl p-4 shadow-inner backdrop-blur-sm"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.3 }}
          >
            <h4 className="text-xs sm:text-sm font-light text-gray-700 flex items-center mb-2 tracking-wide">
              <div className="w-5 h-5 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-inner flex items-center justify-center mr-2">
                <Database className="h-3 w-3 text-white" />
              </div>
              Location Variant Content
            </h4>
            <p className="text-xs text-gray-600 font-light leading-relaxed">
              This content will be used specifically for the "{variant?.name}" location variant. 
              Other location variants will have their own content.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );

  if (isInline) {
    return renderContent();
  }

  return (
    <motion.div 
      className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[10001] flex items-start justify-center overflow-auto pt-10 pb-10"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={handleClose}
    >
      {renderContent()}
    </motion.div>
  );
}; 